import React, { FC, useMemo, useRef, useState, useEffect } from 'react';

import { Button, Flex } from '@chakra-ui/react';

import { useQuery, useQueryClient, useMutation } from 'react-query';
import { fetchCaseHistory } from 'api/history';
import { exportCsvCasessHistory } from 'api/items';

import { Filters } from 'components/primitives/Filters';
import { StatCards } from 'components/primitives/StatCards';
import { DataTable } from 'components/modules/DataTable';

import { washHub } from 'store/slices';
import { useWashHubListener } from 'hooks';

import useQueryParams from 'hooks/useQueryParams';
import useUrlSharing from 'hooks/useUrlSharing';

import { CSVLink } from 'react-csv';

import { History } from 'types/history';
import { DateRangeInputExt } from 'components/modules/DateRangeInputExt';

import { buildCasesHistoryColumns } from './casesHistoryColumns';

export const CasesHistory: FC<{ compType?: string }> = ({
  compType = 'case',
}) => {
  const queryClient = useQueryClient();

  const [page, setPage] = useState<number>(1);
  const [sku, setSku] = useState<string[]>();
  const [venue, setVenue] = useState<string[]>();
  const [status, setStatus] = useState<number[]>();
  const [endDate, setEndDate] = useState<Date>();
  const [startDate, setStartDate] = useState<Date>();
  const [isUrlMapped, setIsUrlMapped] = useState<boolean>(false);
  const [type, setType] = useState<string>();
  const [urlRecord, setUrlRecord] = useState({});
  const queryUrl = useQueryParams();

  const selectedWashHub = washHub.selectors.useSelectedWashHub();
  const washHubId = selectedWashHub?._id || '';
  const washHubName = selectedWashHub?.name || '';
  const isWashHubIdValid = washHub.selectors.useIsWashHubIdValid();
  const isUpdated = useUrlSharing(urlRecord, washHubId);

  const queryKey = [
    `fetch-case-history-${washHubId}`,
    page,
    status,
    sku,
    venue,
    endDate,
    startDate,
  ];

  const queryFn = () =>
    fetchCaseHistory({
      sku,
      venue,
      page,
      status,
      limit: 10,
      washHub: washHubId,
      endDate,
      startDate,
    });

  const options = {
    enabled: isWashHubIdValid,
    keepPreviousData: true,
    refetchOnMount: 'always' as any,
  };

  const { data, isFetching, isLoading } = useQuery(queryKey, queryFn, options);

  useWashHubListener(() => {
    setPage(1);

    setSku(undefined);
    setVenue(undefined);
    setStatus(undefined);
    setStartDate(undefined);
    setEndDate(undefined);
  });

  const history = data?.items || [];
  const totalCount = data?.total || 0;
  const totalPages = data?.totalPages || 0;

  const pagination = {
    page,
    setPage,
    totalCount,
    totalPages,
  };

  useEffect(() => {
    const urlParams = {
      washhubId: String(washHubId),
      compType,
      ...(type ? { type } : {}),
      ...(venue && venue.length ? { venue: venue.join('-') } : {}),
      ...(status && status.length ? { status: status.join('-') } : {}),
      ...(sku && sku.length ? { sku: sku.join('-') } : {}),
      ...(startDate ? { startDate: `${startDate.valueOf()}` } : {}),
      ...(endDate ? { endDate: `${endDate.valueOf()}` } : {}),
    };
    setUrlRecord(urlParams);
  }, [washHubId, compType, status, venue, sku, type, endDate, startDate]);

  useEffect(() => {
    const { status, venue, sku, type, startDate, endDate } = queryUrl;
    status && setStatus(status.split('-').map((el) => +el));
    venue && setVenue(venue.split('-'));
    sku && setSku(sku.split('-'));
    type && setType(type);
    startDate && setStartDate(new Date(+startDate));
    endDate && setEndDate(new Date(+endDate));
    if (isUpdated) setIsUrlMapped(true);
  }, [isUpdated]);

  useEffect(() => () => setUrlRecord({}), []);

  const onApplyFilters = (data: string[], type: string) => {
    setPage(1);
    setType(type);

    if (type === 'sku') setSku(data);
    if (type === 'site') setVenue(data);
  };

  const onClearFilters = () => {
    setPage(1);

    setSku(undefined);
    setVenue(undefined);
  };

  const onUpdateClick = () => {
    onClearFilters();
    setStatus(undefined);

    queryClient.invalidateQueries(`fetch-case-history-${washHubId}`);
  };

  const onSearchPress = (startDate: Date | null, endDate: Date | null) => {
    setPage(1);

    if (startDate) {
      setStartDate(startDate);
    }

    if (endDate) {
      setEndDate(endDate);
    }
  };

  const onSearchClear = () => {
    setPage(1);
    setStartDate(undefined);
    setEndDate(undefined);
  };

  const csvRef = useRef<any>(null);

  const { mutate, data: csvData = '', isLoading: isExporting } = useMutation(
    exportCsvCasessHistory,
    {
      onSuccess: () => {
        setTimeout(() => {
          csvRef.current?.link?.click?.();
        }, 500);
      },
    },
  );

  const columns = useMemo(() => buildCasesHistoryColumns(), []);

  const renderExpBtn = () => {
    return (
      <>
        <Button
          isLoading={isExporting}
          minW="250px"
          onClick={() => {
            mutate({
              sku,
              venue,
              status,
              washHub: washHubId,
              startDate,
              endDate,
            });
          }}>
          Export Cases history to CSV
        </Button>
        {!isExporting && Boolean(csvData) && (
          <CSVLink
            data={csvData}
            ref={csvRef}
            filename={`washhub-${washHubName}-cases-history.csv`}
            target="_blank"
          />
        )}
      </>
    );
  };

  const renderRefreshButton = () => {
    return (
      <Button isLoading={isLoading} ml={2} onClick={onUpdateClick}>
        Update Table
      </Button>
    );
  };

  if (!isUrlMapped) return null;

  return (
    <>
      <DateRangeInputExt
        startDate={startDate}
        endDate={endDate}
        onSearchClear={onSearchClear}
        onSearchPress={onSearchPress}
      />
      <Flex mb={5}>
        {renderExpBtn()}
        {renderRefreshButton()}
      </Flex>
      <StatCards
        hideValue
        type="cases"
        selected={status}
        onSelectStatus={(_selected) => {
          if (Array.isArray(_selected)) {
            setPage(1);
            setStatus(_selected);
          }
        }}
      />
      <Filters
        venue={type ? (type == 'sku' ? sku : venue) : []}
        type="case"
        isFullButton
        onApplyFilters={onApplyFilters}
        onClearFilters={onClearFilters}
      />
      <DataTable<History>
        minW="1700px"
        data={history}
        columns={columns}
        isLoading={isLoading || isFetching}
        pagination={pagination}
      />
    </>
  );
};
